/**
 * Die sieben Tage des Wochenplans.
 *
 * Ein Tag ist hier eine Zeichenkette YYYY-MM-TT in der Zeitzone des Geräts,
 * kein Zeitpunkt. Mit einem Zeitpunkt stünde derselbe Eintrag in Köln am
 * Sonntag und in Auckland am Montag, und der Wochenplan hätte zwei Wahrheiten.
 */

export const WEEKDAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"] as const;

/** YYYY-MM-TT aus einem Datum, in der Zeitzone des Geräts. */
export function asDay(date: Date): string {
  const jahr = date.getFullYear();
  const monat = String(date.getMonth() + 1).padStart(2, "0");
  const tag = String(date.getDate()).padStart(2, "0");
  return `${jahr}-${monat}-${tag}`;
}

export function parseDay(day: string): Date {
  const [jahr, monat, tag] = day.split("-").map(Number);
  return new Date(jahr ?? 1970, (monat ?? 1) - 1, tag ?? 1);
}

/**
 * Die Woche, in der dieser Tag liegt, von Montag bis Sonntag.
 *
 * Montag als erster Tag, weil ein deutscher Wochenplan so aussieht und weil
 * der Wocheneinkauf am Wochenende für die kommende Woche gemacht wird.
 */
export function weekOf(date: Date): string[] {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const verschiebung = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - verschiebung);

  return Array.from({ length: 7 }, (_, i) => {
    const tag = new Date(start);
    tag.setDate(start.getDate() + i);
    return asDay(tag);
  });
}

/** Die Woche danach. Für den Sprung im Wochenplan. */
export function shiftWeek(days: readonly string[], richtung: 1 | -1): string[] {
  const erster = parseDay(days[0] ?? asDay(new Date()));
  erster.setDate(erster.getDate() + 7 * richtung);
  return weekOf(erster);
}

/** "Mo, 21. September" für die Kopfzeile eines Tages. */
export function dayLabel(day: string): string {
  return parseDay(day).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "long" });
}

/** "Montag" für die Spalte im Plan. */
export function weekdayLabel(day: string): string {
  return WEEKDAYS[(parseDay(day).getDay() + 6) % 7] ?? "";
}
